import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  FileText, Eye, Pencil, Tag, Plus, Trash2, GripVertical,
  ChevronUp, ChevronDown, Type, AlignLeft
} from "lucide-react";

const inputCls = "bg-[#0b0811] border-white/10 text-white placeholder:text-slate-600 text-sm";
const CAT_COLORS = { entreprise: "#f7c935", client: "#3b82f6", formateur: "#22c55e", stagiaire: "#8b5cf6", formation: "#f97316", document: "#ef4444", facturation: "#06b6d4" };
const SECTION_OPTS = [
  { key: "header", label: "En-tete", color: "#f7c935" },
  { key: "body", label: "Corps", color: "#3b82f6" },
  { key: "footer", label: "Pied", color: "#22c55e" },
];
const FIELD_TYPES = ["text", "textarea", "select", "checkbox", "date", "number", "email", "phone", "signature", "table", "rich_text"];

export function DocumentEditor({ form, setForm, fields = [], setFields, tags = [] }) {
  const [view, setView] = useState("edit");
  const [activeField, setActiveField] = useState(null);
  const [showTagPanel, setShowTagPanel] = useState(true);

  const tagsByCategory = {};
  (tags || []).forEach(t => { if (!tagsByCategory[t.category]) tagsByCategory[t.category] = []; tagsByCategory[t.category].push(t); });

  const insertTag = (key) => {
    if (!activeField) return;
    setForm(prev => ({ ...prev, [activeField]: (prev[activeField] || "") + " {" + key + "}" }));
  };

  const addField = (section = "body") => {
    setFields([...fields, { id: `f_${Date.now()}`, label: "", type: "text", required: false, section, options: [] }]);
  };
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, key, val) => { const u = [...fields]; u[i][key] = val; setFields(u); };
  const moveField = (i, dir) => {
    const arr = [...fields]; const ni = i + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[i], arr[ni]] = [arr[ni], arr[i]]; setFields(arr);
  };

  const headerFields = fields.filter(f => f.section === "header");
  const bodyFields = fields.filter(f => !f.section || f.section === "body");
  const footerFields = fields.filter(f => f.section === "footer");

  return (
    <div className="flex flex-col h-full" data-testid="document-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-[#161020]">
        <button onClick={() => setView("edit")} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${view === "edit" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:text-white"}`} data-testid="doc-edit-tab">
          <Pencil className="w-3.5 h-3.5 inline mr-1" /> Edition
        </button>
        <button onClick={() => setView("preview")} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${view === "preview" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:text-white"}`} data-testid="doc-preview-tab">
          <Eye className="w-3.5 h-3.5 inline mr-1" /> Apercu
        </button>
        <div className="flex-1" />
        <button onClick={() => setShowTagPanel(!showTagPanel)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${showTagPanel ? "bg-blue-500/20 text-blue-400" : "text-slate-400 hover:text-white"}`} data-testid="doc-tags-toggle">
          <Tag className="w-3.5 h-3.5 inline mr-1" /> Balises
        </button>
        <div className="flex items-center gap-1 ml-2">
          <input type="color" value={form.primary_color || "#461d8a"} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="w-7 h-7 rounded border border-white/10 cursor-pointer" />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Editor */}
        <div className="flex-1 overflow-y-auto p-4">
          {view === "edit" ? (
            <div className="space-y-4 max-w-[800px] mx-auto">
              {/* General */}
              <div className="grid grid-cols-3 gap-3">
                <Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nom du modele" className={inputCls} data-testid="doc-name" />
                <Input value={form.category || ""} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Categorie (Convention, Contrat, PV...)" className={inputCls} />
                <Textarea value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={1} placeholder="Description" className={inputCls} />
              </div>

              {/* EN-TETE */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                <div className="px-4 py-2 flex items-center justify-between" style={{ background: (form.primary_color || "#461d8a") + "10" }}>
                  <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: form.primary_color || "#461d8a" }}>
                    <FileText className="w-3.5 h-3.5" /> En-tete du document
                  </span>
                  <Button size="sm" onClick={() => addField("header")} variant="outline" className="border-white/10 h-6 text-[10px] px-2"><Plus className="w-3 h-3 mr-0.5" /> Champ</Button>
                </div>
                <div className="p-4 space-y-3">
                  <Textarea value={form.doc_header || ""} onFocus={() => setActiveField("doc_header")} onChange={e => setForm({ ...form, doc_header: e.target.value })} rows={3} placeholder="Titre, references, numero du document...&#10;Ex: CONVENTION DE FORMATION PROFESSIONNELLE N{reference}" className={inputCls} />
                  {headerFields.length > 0 && <FieldList fields={headerFields} allFields={fields} setFields={setFields} removeField={removeField} updateField={updateField} moveField={moveField} section="header" />}
                </div>
              </div>

              {/* CORPS */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                <div className="px-4 py-2 bg-blue-500/5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1"><AlignLeft className="w-3.5 h-3.5" /> Corps du document</span>
                  <Button size="sm" onClick={() => addField("body")} variant="outline" className="border-white/10 h-6 text-[10px] px-2"><Plus className="w-3 h-3 mr-0.5" /> Champ</Button>
                </div>
                <div className="p-4 space-y-3">
                  <Textarea value={form.doc_body || ""} onFocus={() => setActiveField("doc_body")} onChange={e => setForm({ ...form, doc_body: e.target.value })} rows={6} placeholder="Contenu principal du document...&#10;Entre les soussignes : {company_name}, represente par...&#10;Et : {client_name}..." className={inputCls} />
                  {bodyFields.length > 0 && <FieldList fields={bodyFields} allFields={fields} setFields={setFields} removeField={removeField} updateField={updateField} moveField={moveField} section="body" />}
                </div>
              </div>

              {/* PIED DE PAGE */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                <div className="px-4 py-2 bg-green-500/5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Pied de page</span>
                  <Button size="sm" onClick={() => addField("footer")} variant="outline" className="border-white/10 h-6 text-[10px] px-2"><Plus className="w-3 h-3 mr-0.5" /> Champ</Button>
                </div>
                <div className="p-4 space-y-3">
                  <Textarea value={form.doc_footer || ""} onFocus={() => setActiveField("doc_footer")} onChange={e => setForm({ ...form, doc_footer: e.target.value })} rows={4} placeholder="Signatures, mentions legales, cachets...&#10;&#10;Fait a _______, le {date_today}&#10;&#10;Signature organisme :                    Signature client :&#10;{signature_blitzz}                        {signature_client}" className={inputCls} />
                  {footerFields.length > 0 && <FieldList fields={footerFields} allFields={fields} setFields={setFields} removeField={removeField} updateField={updateField} moveField={moveField} section="footer" />}
                </div>
              </div>
            </div>
          ) : (
            /* Preview */
            <div className="bg-white rounded-xl shadow-lg max-w-[700px] mx-auto text-black" data-testid="doc-preview">
              <div className="p-8 border-b-4" style={{ borderColor: form.primary_color || "#461d8a" }}>
                <div className="whitespace-pre-wrap text-sm">{form.doc_header || "En-tete du document"}</div>
                {headerFields.map(f => <div key={f.id} className="mt-2 text-xs text-gray-500">[{f.label || "Champ"}]: ______</div>)}
              </div>
              <div className="p-8 min-h-[300px]">
                <div className="whitespace-pre-wrap text-sm">{form.doc_body || "Corps du document"}</div>
                {bodyFields.map(f => (
                  <div key={f.id} className="mt-3 border border-gray-200 rounded-lg p-3">
                    <div className="text-xs font-bold text-gray-600 mb-1">{f.label || "Champ"} {f.required && <span className="text-red-500">*</span>}</div>
                    {f.type === "textarea" || f.type === "rich_text" ? <div className="h-16 bg-gray-50 rounded border border-gray-200" /> : f.type === "signature" ? <div className="h-20 bg-gray-50 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">Signature</div> : f.type === "table" ? <div className="h-16 bg-gray-50 rounded border border-gray-200 flex items-center justify-center text-xs text-gray-400">Tableau</div> : <div className="h-8 bg-gray-50 rounded border border-gray-200" />}
                  </div>
                ))}
              </div>
              <div className="p-8 bg-gray-50 border-t">
                <div className="whitespace-pre-wrap text-sm text-gray-600">{form.doc_footer || "Pied de page"}</div>
                {footerFields.map(f => <div key={f.id} className="mt-2 text-xs text-gray-500">[{f.label || "Champ"}]: ______</div>)}
              </div>
            </div>
          )}
        </div>

        {/* Tags Panel (Digiforma-style) */}
        {showTagPanel && (
          <div className="w-56 border-l border-white/10 bg-[#161020] overflow-y-auto p-3" data-testid="doc-tags-panel">
            <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Balises HTML</div>
            <div className="text-[9px] text-slate-600 mb-3">Cliquez pour inserer dans le champ actif</div>
            {Object.entries(tagsByCategory).map(([cat, catTags]) => (
              <div key={cat} className="mb-3">
                <div className="text-[9px] font-bold uppercase tracking-wider mb-1 px-1 py-0.5 rounded" style={{ color: CAT_COLORS[cat] || "#64748b", background: (CAT_COLORS[cat] || "#64748b") + "10" }}>{cat}</div>
                <div className="space-y-0.5">
                  {catTags.map(t => (
                    <button key={t.key} onClick={() => insertTag(t.key)} className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-left hover:bg-white/5 transition-colors group" data-testid={`doc-tag-${t.key}`}>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: CAT_COLORS[cat] || "#64748b" }} />
                      <span className="text-[10px] text-slate-300 group-hover:text-white flex-1 truncate">{t.label}</span>
                      <span className="text-[8px] text-slate-600 font-mono">{"{" + t.key + "}"}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldList({ fields, allFields, setFields, removeField, updateField, moveField, section }) {
  return (
    <div className="space-y-1.5">
      {fields.map((f) => {
        const globalIdx = allFields.findIndex(af => af.id === f.id);
        return (
          <div key={f.id} className="bg-[#14101c] border border-white/5 rounded-lg p-2 flex items-center gap-2">
            <GripVertical className="w-3 h-3 text-slate-600 flex-shrink-0 cursor-grab" />
            <Input value={f.label} onChange={e => updateField(globalIdx, "label", e.target.value)} placeholder="Libelle" className="bg-transparent border-white/5 text-white text-xs h-7 flex-1" />
            <select value={f.type} onChange={e => updateField(globalIdx, "type", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white text-[10px] rounded px-1.5 py-1 w-24">
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => updateField(globalIdx, "required", !f.required)} className={`text-[9px] px-1.5 py-0.5 rounded ${f.required ? "bg-[#f7c935]/10 text-[#f7c935]" : "text-slate-600"}`}>
              {f.required ? "Req." : "Opt."}
            </button>
            <button onClick={() => removeField(globalIdx)} className="p-0.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
          </div>
        );
      })}
    </div>
  );
}
